import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Container,
  Row,
  Col,
  Card,
  CardBody,
} from 'reactstrap';
import CampaignRow from '../../components/campaign/campaignRow';
import {
  campaignItemsFetchData,
  redirectToCampaignListPage,
} from '../../actions/campaignActions';
import { BeatLoader } from 'react-spinners';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import 'react-datepicker/dist/react-datepicker.css';
import NoRowExistsRow from '../../components/common/noRowExistsRow';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCaretSquareUp,
  faCaretSquareDown,
  faFilter,
  faPlus,
} from '@fortawesome/free-solid-svg-icons';
import sort from 'fast-sort';
import { useNavigate } from 'react-router-dom';

const SortColumns = {
  CAMPAIGN_SOURCE: 'source',
  CAMPAIGN_ID: 'externalId',
  CAMPAIGN_NAME: 'name',
  START_DATE: 'startDate',
  END_DATE: 'endDate',
  STATUS: 'status',
};

const SortOrder = {
  Asc: 'Asc',
  Dsc: 'Dsc',
};

const CampaignPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const campaigns = useSelector((state) => state.campaignItems || []);
  const isLoading = useSelector(
    (state) => state.campaignItemsIsLoading ?? true,
  );

  const initialFormState = {
    startDate: null,
    endDate: null,
    status: '1',
    source: '',
    campaignName: '',
  };
  const [formState, setFormState] = useState(initialFormState);

  const isFormDirty = Object.keys(initialFormState).some(
    (key) => formState[key] !== initialFormState[key],
  );

  const [sortColumn, setSortColumn] = useState(SortColumns.CAMPAIGN_ID);
  const [sortOrder, setSortOrder] = useState(SortOrder.Asc);
  const [isFormValid, setIsFormValid] = useState(false);
  const [emptyRowString, setEmptyRowString] = useState('No Row Exist');
  const [errors, setErrors] = useState({
    source: false,
    campaignName: false,
    startDate: false,
    endDate: false,
    status: false,
  });

  // Memoized fetch function
  const fetchCampaigns = useCallback(() => {
    // Optionally reset error/loading state here
    // dispatch(campaignItemsHasErrored(false));
    // dispatch(campaignItemsIsLoading(true));

    dispatch(
      campaignItemsFetchData(
        formState.source,
        formState.startDate
          ? moment(formState.startDate).format('DD-MMM-YYYY')
          : null,
        formState.endDate
          ? moment(formState.endDate).format('DD-MMM-YYYY')
          : null,
        formState.campaignName,
        formState.status,
      ),
    );
  }, [dispatch, formState]);

  // Fetch campaigns on mount and when formState changes
  useEffect(() => {
    fetchCampaigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Validate form
  const validateForm = useCallback(() => {
    const newErrors = {};
    const sDate = formState.startDate
      ? moment(formState.startDate).format('DD-MMM-YYYY')
      : null;
    const eDate = formState.endDate
      ? moment(formState.endDate).format('DD-MMM-YYYY')
      : null;

    if (sDate && eDate) {
      const isInvalid = moment(eDate, 'DD-MMM-YYYY').isSameOrBefore(
        moment(sDate, 'DD-MMM-YYYY'),
      );
      newErrors.startDate = isInvalid;
      newErrors.endDate = isInvalid;
      if (isInvalid) alert('Invalid date');
    }

    setIsFormValid(!newErrors.startDate && !newErrors.endDate);
    setErrors(newErrors);
    return !newErrors.startDate && !newErrors.endDate;
  }, [formState]);

  // Validate form on formState change
  useEffect(() => {
    validateForm();
  }, [formState, validateForm]);

  // Sorting campaigns
  const sortedCampaigns = (() => {
    const campaignsCopy = [...campaigns]; // create a shallow copy to avoid mutation
    if (sortOrder === SortOrder.Asc) {
      return sort(campaignsCopy).asc((o) => o[sortColumn]);
    }
    if (sortOrder === SortOrder.Dsc) {
      return sort(campaignsCopy).desc((o) => o[sortColumn]);
    }
  })();

  const handleInputChange = ({ target: { name, value, type, checked } }) => {
    setFormState((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleDateChange = (name, date) => {
    setFormState((prev) => ({ ...prev, [name]: date }));
  };

  const handleSubmitSearchForm = (e) => {
    e.preventDefault();
    setEmptyRowString('No data has been found for the specified selection');
    fetchCampaigns();
  };

  const gotoAddNewPage = () => {
    dispatch(redirectToCampaignListPage(false));
    navigate('/campaign/add-campaign');
  };

  const getSortButton = (column) => (
    <span>
      <FontAwesomeIcon
        icon={
          sortColumn === column && sortOrder === SortOrder.Asc
            ? faCaretSquareUp
            : faCaretSquareDown
        }
      />
    </span>
  );

  const onSort = (e, sortKey) => {
    setSortColumn(sortKey);
    setSortOrder((prev) =>
      prev === SortOrder.Asc ? SortOrder.Dsc : SortOrder.Asc,
    );
  };

  const campaignRows =
    sortedCampaigns.length > 0
      ? sortedCampaigns.map((campaign) => (
          <div className='panel panel-default' key={campaign.id}>
            <CampaignRow campaign={campaign} />
          </div>
        ))
      : !isLoading && <NoRowExistsRow emptyRowText={emptyRowString} />;

  return (
    <div>
      <h4>Manage Campaigns</h4>
      <Card>
        <CardBody>
          <Container fluid>
            <Row>
              <Col className='p-0'>
                <Form onSubmit={handleSubmitSearchForm}>
                  <div className='d-flex justify-content-left align-items-center flex-wrap gap-0'>
                    <FormGroup className='d-flex align-items-center me-2'>
                      <Label for='source' className='mb-0 me-2'>
                        Source
                      </Label>
                      <Input
                        type='select'
                        name='source'
                        id='source'
                        onChange={handleInputChange}
                        value={formState.source}
                        className={
                          errors.source
                            ? 'form-control error mb-0'
                            : 'form-control mb-0'
                        }
                      >
                        <option value=''>All</option>
                        <option value='SB'>StarBos</option>
                        <option value='VE'>Vynamic Engage</option>
                      </Input>
                    </FormGroup>
                    <FormGroup className='d-flex align-items-center me-2'>
                      <Label for='campaignName' className='mb-0 me-2'>
                        Campaign Name
                      </Label>
                      <Input
                        id='campaignName'
                        name='campaignName'
                        onChange={handleInputChange}
                        value={formState.campaignName}
                        style={{ width: '205px' }}
                        className={
                          errors.campaignName
                            ? 'form-control error mb-0'
                            : 'form-control mb-0'
                        }
                      />
                    </FormGroup>
                    <FormGroup className='d-flex align-items-center me-2'>
                      <Label for='startDate' className='mb-0 me-2'>
                        Start Date
                      </Label>
                      <DatePicker
                        id='startDate'
                        name='startDate'
                        selected={formState.startDate}
                        dateFormat='DD-MMM-YYYY'
                        onChange={(date) => handleDateChange('startDate', date)}
                        className={errors.startDate ? 'error' : 'form-control'}
                        locale='en-au'
                      />
                    </FormGroup>
                    <FormGroup className='d-flex align-items-center me-2'>
                      <Label for='endDate' className='mb-0 me-2'>
                        End Date
                      </Label>
                      <DatePicker
                        id='endDate'
                        name='endDate'
                        selected={formState.endDate}
                        dateFormat='DD-MMM-YYYY'
                        onChange={(date) => handleDateChange('endDate', date)}
                        className={errors.endDate ? 'error' : 'form-control'}
                      />
                    </FormGroup>
                    <FormGroup className='d-flex align-items-center me-2'>
                      <Label for='status' className='mb-0 me-2'>
                        Status
                      </Label>
                      <Input
                        type='select'
                        name='status'
                        id='status'
                        onChange={handleInputChange}
                        value={formState.status}
                        className={errors.status ? 'error ' : ''}
                      >
                        <option value=''>All</option>
                        <option value='1'>Active</option>
                        <option value='0'>Inactive</option>
                      </Input>
                    </FormGroup>
                  </div>
                  <div className='d-flex flex-column align-items-start gap-2'>
                    <Button
                      color='primary'
                      name='btnFilter'
                      id='btnFilter'
                      disabled={isLoading || !isFormDirty || !isFormValid}
                      className='me-2 '
                    >
                      <FontAwesomeIcon icon={faFilter} />
                      Filter
                    </Button>
                    <Button
                      color='primary'
                      name='btnAddNew'
                      id='btnAddNew'
                      onClick={gotoAddNewPage}
                    >
                      <FontAwesomeIcon icon={faPlus} /> Add New
                    </Button>
                  </div>
                </Form>
              </Col>
            </Row>
          </Container>
        </CardBody>
      </Card>
      <h6 className='mt-2'>Campaigns</h6>
      <Container fluid>
        <Row>
          <Col
            md='1'
            className='column-header white-border'
            onClick={(e) => onSort(e, SortColumns.CAMPAIGN_SOURCE)}
          >
            Source {getSortButton(SortColumns.CAMPAIGN_SOURCE)}
          </Col>
          <Col
            md='1'
            className='column-header white-border'
            onClick={(e) => onSort(e, SortColumns.CAMPAIGN_ID)}
          >
            Id {getSortButton(SortColumns.CAMPAIGN_ID)}
          </Col>
          <Col
            md='5'
            className='column-header white-border'
            onClick={(e) => onSort(e, SortColumns.CAMPAIGN_NAME)}
          >
            Name - Description {getSortButton(SortColumns.CAMPAIGN_NAME)}
          </Col>
          <Col
            md='1'
            className='column-header white-border'
            onClick={(e) => onSort(e, SortColumns.START_DATE)}
          >
            Start Date {getSortButton(SortColumns.START_DATE)}
          </Col>
          <Col
            md='1'
            className='column-header white-border'
            onClick={(e) => onSort(e, SortColumns.END_DATE)}
          >
            End Date {getSortButton(SortColumns.END_DATE)}
          </Col>
          <Col
            md='1'
            className='column-header white-border'
            onClick={(e) => onSort(e, SortColumns.STATUS)}
          >
            Status {getSortButton(SortColumns.STATUS)}
          </Col>
          <Col md='2' className='column-header white-border'>
            {sortColumn}/{sortOrder}
          </Col>
        </Row>
        {campaignRows}
        <Row>
          <Col>
            <BeatLoader color={'#004C5F'} loading={isLoading} />
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default CampaignPage;
